import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import cloudIcon from "../../../assets/images/logo.png";
import { useAuth } from "../../hooks/useAuth";
import { MdOutlineBackup } from "react-icons/md";

const navItems = [
  {
    id: "overview",
    label: "Overview",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        className="w-6 h-6"
        stroke="currentColor"
        strokeWidth={1.8}
      >
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </svg>
    ),
  },
  {
    id: "products",
    label: "Products",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        className="w-6 h-6"
        stroke="currentColor"
        strokeWidth={1.8}
      >
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    ),
  },
  /*{
    id: "troubleshoot",
    label: "Troubleshoot",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        className="w-6 h-6"
        stroke="currentColor"
        strokeWidth={1.8}
      >
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
      </svg>
    ),
  },*/
  {
    id: "alert",
    label: "Alerts",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        className="w-6 h-6"
        stroke="currentColor"
        strokeWidth={1.8}
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
  },
  {
    id: "financial",
    label: "Financial",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        className="w-6 h-6"
        stroke="currentColor"
        strokeWidth={1.8}
      >
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        <line x1="4" y1="11" x2="20" y2="11" />
        <line x1="4" y1="15" x2="12" y2="15" />
      </svg>
    ),
  },
  {
    id: "backups",
    label: "Backups",
    icon: <MdOutlineBackup className="w-6 h-6" />,
  },
  {
    id: "monthly-report", 
    label: "Monthly Report",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        className="w-6 h-6"
        stroke="currentColor"
        strokeWidth={1.8}
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
  },
];

const Sidebar = ({ mobileOnClose }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      navigate("/login", { replace: true });
      if (mobileOnClose) mobileOnClose();
    }
  };

  const handleNavigation = (id) => {
    navigate(`/dashboard/${id}`);
    if (mobileOnClose) mobileOnClose();
  };

  return (
    <div
      className={`
        relative flex flex-col h-screen shadow-2xl
        transition-all duration-300 ease-in-out
        ${isCollapsed ? "w-[80px]" : "w-[280px]"}
        bg-primary-dark
      `}
      style={{ overflowX: "hidden", overflowY: "auto" }}
    >
      <div
        className={`flex items-center px-5 pt-2 pb-2 ${isCollapsed ? "justify-center" : "justify-between"}`}
      >
        <div
          className={`flex items-center gap-3 ${isCollapsed ? "justify-center w-full" : ""}`}
        >
          <img
            src={cloudIcon}
            alt="Cloud Icon"
            className="w-12 pt-4 pb-4 object-contain brightness-0 invert"
          />
          {!isCollapsed && (
            <div className="text-white text-2xl font-bold leading-tight">
              Sindi<span className="text-primary-cyan">Board</span>
            </div>
          )}
        </div>
        {!isCollapsed && (
          <button
            onClick={() => setIsCollapsed(true)}
            onMouseEnter={() => setHoveredItem("__toggle")}
            onMouseLeave={() => setHoveredItem(null)}
            className={`p-1.5 rounded-lg transition-all duration-200 hover:scale-105
              ${hoveredItem === "__toggle" ? "bg-card-bg text-primary-cyan" : "text-gray-400"}
            `}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              className="w-5 h-5"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        )}
      </div>
      {isCollapsed && (
        <div className="flex justify-center mb-4">
          <button
            onClick={() => setIsCollapsed(false)}
            onMouseEnter={() => setHoveredItem("__toggle")}
            onMouseLeave={() => setHoveredItem(null)}
            className={`p-2 rounded-lg transition-all duration-200
              ${hoveredItem === "__toggle" ? "bg-card-bg text-primary-cyan" : "text-gray-400"}
            `}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              className="w-5 h-5"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      )}
      <div className="mb-5 border-t border-border-color" />
      <nav className="flex-1 flex flex-col gap-2 px-3 overflow-y-visible">
        {navItems.map((item) => {
          const isActive = currentPath === `/dashboard/${item.id}`;
          const isHovered = hoveredItem === item.id;
          return (
            <div key={item.id} className="relative">
              <button
                onClick={() => handleNavigation(item.id)}
                onMouseEnter={() => setHoveredItem(item.id)}
                onMouseLeave={() => setHoveredItem(null)}
                className={`
                  relative w-full flex items-center gap-3 rounded-xl
                  transition-all duration-200 ease-in-out group
                  ${isCollapsed ? "justify-center px-2 py-3" : "px-4 py-3"}
                  ${isActive ? "bg-primary-cyan/20 border border-primary-cyan/30" : ""}
                  ${!isActive && isHovered ? "bg-card-bg" : ""}
                  ${!isActive && !isHovered ? "bg-transparent border border-transparent" : ""}
                `}
                style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
              >
                {isActive && (
                  <span
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full bg-primary-cyan"
                    style={{
                      height: "40%",
                      boxShadow: "0 0 10px rgba(220, 38, 38, 0.8)",
                    }}
                  />
                )}
                <span
                  className={`flex-shrink-0 transition-all duration-200 ${isHovered && !isActive ? "scale-110" : "scale-100"}
                    ${isActive ? "text-primary-cyan" : ""}
                    ${!isActive && isHovered ? "text-primary-cyan" : ""}
                    ${!isActive && !isHovered ? "text-gray-400" : ""}
                  `}
                >
                  {item.icon}
                </span>
                <span
                  className={`
                    text-[15px] font-medium whitespace-nowrap overflow-hidden
                    transition-all duration-300
                    ${isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100"}
                    ${isActive ? "text-primary-cyan" : ""}
                    ${!isActive && isHovered ? "text-primary-dark" : ""}
                    ${!isActive && !isHovered ? "text-gray-500" : ""}
                  `}
                  style={{
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  }}
                >
                  {item.label}
                </span>
              </button>
              {isCollapsed && isHovered && (
                <div
                  className="absolute left-full top-1/2 -translate-y-1/2 ml-3 z-50 pointer-events-none hidden lg:block"
                  style={{
                    animation: "fadeSlideIn 0.2s cubic-bezier(0.4, 0, 0.2, 1) forwards",
                  }}
                >
                  <div
                    className="px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap shadow-xl bg-card-bg border border-primary-cyan/40 text-primary-cyan"
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "13px",
                      letterSpacing: "0.01em",
                    }}
                  >
                    {item.label}
                    <span className="absolute right-full top-1/2 -translate-y-1/2 border-[5px] border-transparent border-r-card-bg" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div className="px-3 pb-6 pt-3">
        <div className="mb-4 mx-2 border-t border-border-color" />
        {!isCollapsed && (
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 bg-primary-cyan hover:bg-primary-cyan/90 group"
            style={{ touchAction: "manipulation" }}
          >
            <span
              className="text-white font-semibold text-m"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Logout
            </span>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              className="w-6 h-5.5 text-white"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
        {isCollapsed && (
          <div className="flex justify-center mt-2">
            <button
              onClick={handleLogout}
              className="flex justify-center items-center p-3 rounded-xl transition-all duration-200 hover:bg-card-bg group"
              title="Logout"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                className="w-5 h-5 text-gray-400 group-hover:text-primary-cyan transition-colors duration-200"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;